import { piecePoints, SCENE, type GlassIntent, type GlassState } from "./stained-glass-core";

export function archPath(outset = 0): Path2D {
  const path = new Path2D();
  path.moveTo(SCENE.left - outset, SCENE.bottom + outset);
  path.lineTo(SCENE.left - outset, SCENE.spring);
  path.ellipse(SCENE.center, SCENE.spring, SCENE.radius + outset, SCENE.archHeight + outset, 0, Math.PI, Math.PI * 2);
  path.lineTo(SCENE.right + outset, SCENE.bottom + outset);
  path.closePath();
  return path;
}

export function glassPath(piece: GlassIntent): Path2D {
  const path = new Path2D();
  if (piece.shape === "circle") path.arc(piece.x, piece.y, piece.size / 2, 0, Math.PI * 2);
  else {
    piecePoints(piece).forEach((p, index) => index ? path.lineTo(p.x, p.y) : path.moveTo(p.x, p.y));
    path.closePath();
  }
  return path;
}

function paintPane(ctx: CanvasRenderingContext2D, pieces: GlassIntent[], preview: GlassIntent | null) {
  const arch = archPath();
  ctx.save();
  ctx.clip(arch);
  const clearGlass = ctx.createLinearGradient(SCENE.left, SCENE.top, SCENE.right, SCENE.bottom);
  clearGlass.addColorStop(0, "#fffcf1"); clearGlass.addColorStop(.5, "#f2f1dc"); clearGlass.addColorStop(1, "#e2e6de");
  ctx.fillStyle = clearGlass;
  ctx.fillRect(SCENE.left, SCENE.top, SCENE.right - SCENE.left, SCENE.bottom - SCENE.top);
  const all = preview ? [...pieces, preview] : pieces;
  // Multiply models successive colored filters: overlapping panes absorb more light.
  // Leading is drawn afterwards so it remains charcoal even under later pieces.
  for (const piece of all) {
    ctx.globalCompositeOperation = "multiply";
    ctx.globalAlpha = piece === preview ? .58 : .76;
    const glass = ctx.createLinearGradient(piece.x - piece.size / 2, piece.y - piece.size / 2, piece.x + piece.size / 2, piece.y + piece.size / 2);
    glass.addColorStop(0, piece.color); glass.addColorStop(.47, piece.color + "df"); glass.addColorStop(1, piece.color);
    ctx.fillStyle = glass;
    ctx.fill(glassPath(piece));
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  for (const piece of all) {
    const path = glassPath(piece);
    ctx.lineJoin = "round";
    ctx.lineWidth = piece === preview ? 2 : 2.6;
    ctx.strokeStyle = piece === preview ? "#fffdf5" : "#302f2e";
    ctx.setLineDash(piece === preview ? [7, 5] : []);
    ctx.stroke(path);
    if (piece !== preview) {
      ctx.save();
      ctx.clip(path);
      ctx.strokeStyle = "#ffffff38";
      ctx.lineWidth = .9;
      ctx.translate(1.4, 1.4);
      ctx.stroke(path);
      ctx.restore();
    }
  }
  ctx.setLineDash([]);
  // Fine, deterministic texture, without image assets or per-frame randomness.
  ctx.fillStyle = "#ffffff15";
  for (let y = SCENE.top; y < SCENE.bottom; y += 7) for (let x = SCENE.left; x < SCENE.right; x += 9) {
    ctx.fillRect(x + Math.sin(y * x) * 2, y, 1.1, 1.1);
  }
  const shine = ctx.createLinearGradient(SCENE.left, SCENE.top, SCENE.right, SCENE.bottom);
  shine.addColorStop(0, "#ffffff30"); shine.addColorStop(.45, "#ffffff00"); shine.addColorStop(1, "#ffffff13");
  ctx.fillStyle = shine; ctx.fill(arch);
  ctx.restore();
}

export function drawGlassScene(ctx: CanvasRenderingContext2D, state: GlassState, preview: GlassIntent | null = null, sunlight = state.finished) {
  const arch = archPath();
  const height = sunlight ? SCENE.presentationHeight : SCENE.height;
  const background = ctx.createLinearGradient(SCENE.originX, 0, SCENE.right, height);
  background.addColorStop(0, sunlight ? "#fff7e5" : "#eee9df");
  background.addColorStop(.6, "#e8e2d6"); background.addColorStop(1, "#d9d0be");
  ctx.fillStyle = background; ctx.fillRect(SCENE.originX, 0, SCENE.width, height);
  if (sunlight) {
    const glow = ctx.createRadialGradient(SCENE.center + 230, 170, 5, SCENE.center + 230, 170, 1000);
    glow.addColorStop(0, "#fffef4dc"); glow.addColorStop(1, "#fff4cc00");
    ctx.fillStyle = glow; ctx.fillRect(SCENE.originX, 0, SCENE.width, height);
    const projection = document.createElement("canvas");
    projection.width = SCENE.width; projection.height = SCENE.height;
    const projectionContext = projection.getContext("2d")!;
    projectionContext.translate(-SCENE.originX, 0);
    paintPane(projectionContext, state.pieces, null);
    ctx.save();
    ctx.translate(SCENE.left - 20, SCENE.bottom + 40); ctx.transform(.85, .03, .65, .14, 0, 0);
    ctx.globalAlpha = .34; ctx.filter = "blur(8px)";
    const paneWidth = SCENE.right - SCENE.left, paneHeight = SCENE.bottom - SCENE.top;
    ctx.drawImage(projection, SCENE.left - SCENE.originX, SCENE.top, paneWidth, paneHeight, 0, 0, paneWidth, paneHeight);
    ctx.restore();
  }
  // Soft architectural shadow and an inset brass seam in the substantial dark frame.
  // Keep the entire frame outside the aperture: a valid clipped sliver stays visible.
  const outerFrame = archPath(27);
  ctx.save();
  ctx.shadowColor = "#29261e55"; ctx.shadowBlur = 25; ctx.shadowOffsetY = 13;
  ctx.fillStyle = "#302f2e"; ctx.fill(outerFrame);
  ctx.restore();
  paintPane(ctx, state.pieces, preview);
  const frame = ctx.createLinearGradient(SCENE.left - 30, 0, SCENE.right + 30, SCENE.bottom + 30);
  frame.addColorStop(0, "#393b3a"); frame.addColorStop(.45, "#262928"); frame.addColorStop(1, "#454540");
  const frameRing = new Path2D(outerFrame); frameRing.addPath(arch);
  ctx.fillStyle = frame; ctx.fill(frameRing, "evenodd");
  ctx.lineWidth = 1; ctx.strokeStyle = "#ad967866"; ctx.stroke(archPath(13));
  ctx.lineWidth = 1.5; ctx.strokeStyle = "#242824"; ctx.stroke(arch);
  ctx.fillStyle = "#2c302e"; ctx.fillRect(SCENE.left - 31, SCENE.bottom + 25, SCENE.right - SCENE.left + 62, 10);
  ctx.fillStyle = "#bbaa8638"; ctx.fillRect(SCENE.left - 31, SCENE.bottom + 25, SCENE.right - SCENE.left + 62, 1);
  if (sunlight) {
    ctx.save(); ctx.clip(arch);
    const beam = ctx.createLinearGradient(SCENE.left + 60, SCENE.top, SCENE.right - 60, SCENE.bottom);
    beam.addColorStop(0, "#fffbe730"); beam.addColorStop(.45, "#fffbe710"); beam.addColorStop(1, "#fffbe700");
    ctx.fillStyle = beam; ctx.fill(arch); ctx.restore();
    ctx.textAlign = "center"; ctx.fillStyle = "#3b3933"; ctx.font = "38px Georgia, serif";
    const words = state.title.split(/\s+/); const lines: string[] = []; let line = "";
    for (const word of words) {
      if (line && ctx.measureText(line + " " + word).width > SCENE.width - 110) { lines.push(line); line = word; }
      else line += (line ? " " : "") + word;
    }
    if (line) lines.push(line);
    lines.slice(0, 2).forEach((text, index) => ctx.fillText(text, SCENE.center, SCENE.bottom + 235 + index * 39, SCENE.width - 110));
    ctx.font = "17px 'Segoe UI', sans-serif"; ctx.fillStyle = "#706756";
    ctx.fillText("MADE TOGETHER · " + state.players.join(" & "), SCENE.center, SCENE.presentationHeight - (lines.length > 1 ? 25 : 60), SCENE.width - 110);
  }
}

export function renderGlassCanvas(canvas: HTMLCanvasElement, state: GlassState, preview: GlassIntent | null = null) {
  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const height = state.finished ? SCENE.presentationHeight : SCENE.height;
  const scale = Math.min(canvas.width / SCENE.width, canvas.height / height);
  ctx.fillStyle = "#e8e2d6"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.translate((canvas.width - SCENE.width * scale) / 2 - SCENE.originX * scale, (canvas.height - height * scale) / 2);
  ctx.scale(scale, scale);
  drawGlassScene(ctx, state, preview);
}

export function glassExportCanvas(state: GlassState): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = SCENE.width * 2; canvas.height = SCENE.presentationHeight * 2;
  renderGlassCanvas(canvas, { ...state, finished: true });
  return canvas;
}
